import {
  Box,
  Button,
  Card,
  Field,
  Flex,
  Heading,
  NativeSelect,
  Popover,
  Portal,
  Separator,
  Tag,
  Text,
} from "@chakra-ui/react";

import { useCallback, useState } from "react";
import { LuCheck, LuPlus, LuX } from "react-icons/lu";

import {
  CUSTOM_SERVICE_OPTIONS,
  DAYS_OPTIONS,
  selectFieldProps,
} from "@features/services/travel/types";
import type { PackageType } from "@features/services/travel/types";

const PackageCard = ({
  title,
  description,
  details,
  isSelected,
  onSelect,
}: {
  title: string;
  description: string;
  details: string[];
  isSelected: boolean;
  onSelect: () => void;
}) => (
  <Card.Root
    flex={1}
    minW={0}
    display="flex"
    flexDirection="column"
    cursor="pointer"
    borderWidth="2px"
    borderColor={isSelected ? "selection.500" : "border.dark"}
    bg="surface.background"
    _hover={{ borderColor: isSelected ? "selection.500" : "border.dark" }}
    transition="border-color 0.15s"
    onClick={onSelect}
  >
    <Card.Body gap={0} display="flex" flexDirection="column" flex={1}>
      <Flex flexDirection="column" flex={1}>
        <Card.Title color="fg">{title}</Card.Title>
        <Text
          fontSize="sm"
          color="text.secondary"
          mt={1}
          h="2.5rem"
          overflow="hidden"
        >
          {description}
        </Text>
        <Separator my={4} borderColor="border.dark" />
        <Flex flexDirection="column" gap={2.5} flex={1}>
          {details.map((item) => (
            <Flex key={item} align="center" gap={2}>
              <Box
                as={LuCheck}
                boxSize={4}
                color="selection.500"
                flexShrink={0}
              />
              <Text fontSize="sm" color="fg">
                {item}
              </Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Button
        mt={5}
        size="sm"
        variant={isSelected ? "solid" : "outline"}
        w="full"
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
      >
        {isSelected ? "Selected" : "Select"}
      </Button>
    </Card.Body>
  </Card.Root>
);

const CustomServicePicker = ({
  selected,
  onAdd,
  onRemove,
}: {
  selected: string[];
  onAdd: (service: string) => void;
  onRemove: (service: string) => void;
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const available = CUSTOM_SERVICE_OPTIONS.filter(
    (s) => !selected.includes(s),
  );

  const onPopoverToggle = useCallback(
    (details: { open: boolean }) => {
      setPopoverOpen(details.open);
    },
    [],
  );

  const onServiceClick = useCallback(
    (service: string) => {
      onAdd(service);
      if (available.length <= 1) {
        setPopoverOpen(false);
      }
    },
    [onAdd, available.length],
  );

  return (
    <Flex flexDirection="column" gap={3}>
      <Flex flexDirection="column" gap={1}>
        <Text fontSize="sm" fontWeight="medium" color="fg">
          Select your services
        </Text>
        <Text fontSize="xs" color="text.secondary">
          Choose the services you need for your trip. You can add or remove
          services at any time.
        </Text>
      </Flex>
      <Flex flexWrap="wrap" gap={2} alignItems="center">
        {selected.map((service) => (
          <Tag.Root
            key={service}
            size="lg"
            borderRadius="full"
            variant="outline"
            borderColor="border.dark"
            px={3}
            py={1}
          >
            <Tag.Label color="fg" fontSize="sm">
              {service}
            </Tag.Label>
            <Tag.EndElement>
              <Box
                as={LuX}
                boxSize={3.5}
                color="fg.muted"
                cursor="pointer"
                onClick={() => onRemove(service)}
              />
            </Tag.EndElement>
          </Tag.Root>
        ))}
        {available.length > 0 && (
          <Popover.Root
            open={popoverOpen}
            onOpenChange={onPopoverToggle}
            lazyMount
            positioning={{ placement: "bottom-start" }}
          >
            <Popover.Trigger asChild>
              <Button
                size="sm"
                variant="outline"
                borderRadius="full"
                borderColor="border.dark"
              >
                <Flex as="span" align="center" gap={1.5}>
                  <LuPlus size={14} /> Add service
                </Flex>
              </Button>
            </Popover.Trigger>
            <Portal>
              <Popover.Positioner>
                <Popover.Content
                  p={2}
                  minW="280px"
                  bgColor="surface.background"
                >
                  <Flex flexDirection="column" gap={0.5}>
                    {available.map((service) => (
                      <Button
                        key={service}
                        variant="ghost"
                        size="sm"
                        justifyContent="flex-start"
                        fontWeight="normal"
                        onClick={() => onServiceClick(service)}
                      >
                        {service}
                      </Button>
                    ))}
                  </Flex>
                </Popover.Content>
              </Popover.Positioner>
            </Portal>
          </Popover.Root>
        )}
      </Flex>
    </Flex>
  );
};

const ChoosePackageStep = ({
  selectedDays,
  selectedPackage,
  customServices,
  onDaysChange,
  onPackageSelect,
  onAddCustomService,
  onRemoveCustomService,
}: {
  selectedDays: string;
  selectedPackage: PackageType;
  customServices: string[];
  onDaysChange: (value: string) => void;
  onPackageSelect: (pkg: PackageType) => void;
  onAddCustomService: (service: string) => void;
  onRemoveCustomService: (service: string) => void;
}) => (
  <Flex flexDirection="column" gap={6} mt={8}>
    <Flex flexDirection="column" gap={1}>
      <Heading size="lg" color="fg">
        Choose your package
      </Heading>
      <Text color="text.secondary" fontSize="sm">
        Select the package that best fits your needs.
      </Text>
    </Flex>
    <Flex
      gap={4}
      flexDirection={{ base: "column", md: "row" }}
      alignItems="stretch"
    >
      <PackageCard
        title="Standard"
        description="Essential features for your China travel"
        details={["No breakfast", "Best available airline"]}
        isSelected={selectedPackage === "standard"}
        onSelect={() => onPackageSelect("standard")}
      />
      <PackageCard
        title="Premium"
        description="Full features with priority support"
        details={["Breakfast included", "Fly with Emirates"]}
        isSelected={selectedPackage === "premium"}
        onSelect={() => onPackageSelect("premium")}
      />
      <PackageCard
        title="Custom"
        description="Build your own package with the services you need"
        details={
          customServices.length > 0
            ? [
                ...customServices.slice(0, 4),
                ...(customServices.length > 4
                  ? [`and ${customServices.length - 4} more`]
                  : []),
              ]
            : ["Pick your own services"]
        }
        isSelected={selectedPackage === "custom"}
        onSelect={() => onPackageSelect("custom")}
      />
    </Flex>
    {(selectedPackage === "standard" || selectedPackage === "premium") && (
      <Field.Root maxW={{ base: "full", md: "xs" }}>
        <Field.Label color="fg">Duration</Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field
            value={selectedDays}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              onDaysChange(e.target.value)
            }
            {...selectFieldProps}
          >
            {DAYS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>
    )}
    {selectedPackage === "custom" && (
      <CustomServicePicker
        selected={customServices}
        onAdd={onAddCustomService}
        onRemove={onRemoveCustomService}
      />
    )}
  </Flex>
);

export default ChoosePackageStep;
